import React, { useState } from "react";
import SectionLabel from "../../components/SectionLabel";
import "./NextSteps.css";

const STEP_COPY = {
  amicable_negotiation: {
    title: "Negociação amigável",
    summary:
      "Envie a carta direto para a companhia aérea (SAC, e-mail ou formulário). Costuma ser o caminho mais rápido — guarde o protocolo.",
    cta: "Como abrir a negociação",
    ctaWithLabel: (label) => `Abrir SAC da ${label}`,
  },
  consumer_gov_br: {
    title: "Reclamação no consumidor.gov.br",
    summary:
      "Plataforma oficial do governo. A empresa tem 10 dias para responder. Crie conta, descreva o caso e anexe a carta.",
    cta: "Abrir consumidor.gov.br",
  },
  reclame_aqui: {
    title: "Reclamação no Reclame Aqui",
    summary:
      "Pressão pública e reputacional. Funciona bem quando combinado com os canais oficiais.",
    cta: "Abrir Reclame Aqui",
  },
  anac: {
    title: "Reclamação na ANAC",
    summary:
      "A ANAC é o regulador do setor. Pode multar a companhia e costuma acelerar a resposta.",
    cta: "Abrir canal da ANAC",
  },
  procon_jec: {
    title: "Procon e Juizado Especial Cível",
    summary:
      "Último recurso. Procon resolve administrativamente; o JEC permite ação até 20 salários mínimos sem advogado.",
    cta: "Saber como abrir",
  },
};

const STATUS_COPY = {
  pending: { label: "A fazer", tone: "mute" },
  in_progress: { label: "Em andamento", tone: "warn" },
  skipped: { label: "Pulado", tone: "mute" },
  completed: { label: "Concluído", tone: "ok" },
};

export default function NextSteps({
  case: caseData,
  steps: initialSteps = [],
  complete_path: completePath,
  case_resolved: initialResolved = false,
  can_complete: initialCanComplete = false,
}) {
  const [steps, setSteps] = useState(initialSteps);
  const [resolved, setResolved] = useState(initialResolved);
  const [canComplete, setCanComplete] = useState(initialCanComplete);
  const [busyKind, setBusyKind] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState(null);

  const updateStep = async (step, status) => {
    if (resolved || busyKind) return;
    setBusyKind(step.kind);
    setError(null);
    try {
      const data = await postJson(step.update_path, { status }, "PATCH");
      setSteps(data.steps);
      setCanComplete(data.can_complete);
      setResolved(data.case_resolved);
    } catch {
      setError("Não foi possível atualizar o passo. Tente novamente.");
    } finally {
      setBusyKind(null);
    }
  };

  const completeCase = async () => {
    if (!canComplete || completing || resolved) return;
    setCompleting(true);
    setError(null);
    try {
      const data = await postJson(completePath, {}, "POST");
      setSteps(data.steps);
      setResolved(data.case_resolved);
      setCanComplete(data.can_complete);
    } catch {
      setError("Não foi possível concluir o caso. Tente novamente.");
    } finally {
      setCompleting(false);
    }
  };

  return (
    <main className="trunfo-steps">
      <header className="trunfo-steps__top">
        <SectionLabel tone="mute">CASO {caseData.public_code} · PRÓXIMOS PASSOS</SectionLabel>
        <h1 className="trunfo-steps__title">
          {resolved ? (
            <>
              Caso <em>concluído.</em>
            </>
          ) : (
            <>
              Hora de <em>agir.</em>
            </>
          )}
        </h1>
        <p className="trunfo-steps__lede">
          {resolved
            ? "Você marcou esse caso como resolvido. Pode revisitar a qualquer momento."
            : "Comece pela negociação amigável. Se não der certo, escale na ordem abaixo. Você pode pular qualquer passo."}
        </p>
        <div className="trunfo-steps__top-actions">
          <a className="trunfo-btn" href={caseData.letter_path}>
            ← Voltar à carta
          </a>
          <a className="trunfo-btn" href={caseData.back_path}>
            Ver caso completo
          </a>
        </div>
      </header>

      {error ? (
        <p className="trunfo-steps__error" role="alert">
          {error}
        </p>
      ) : null}

      <ol className="trunfo-steps__list">
        {steps.map((step) => {
          const copy = STEP_COPY[step.kind] ?? { title: step.kind, summary: "", cta: "Abrir" };
          const status = STATUS_COPY[step.status] ?? STATUS_COPY.pending;
          const isBusy = busyKind === step.kind;
          const isLocked = resolved || (busyKind && !isBusy);
          return (
            <li key={step.kind} className={`trunfo-steps__item is-${step.status}`}>
              <div className="trunfo-steps__item-head">
                <span className="trunfo-steps__position">
                  {String(step.position).padStart(2, "0")}
                </span>
                <div className="trunfo-steps__item-text">
                  <h2 className="trunfo-steps__item-title">{copy.title}</h2>
                  <p className="trunfo-steps__item-summary">{copy.summary}</p>
                </div>
                <span className={`trunfo-steps__badge trunfo-steps__badge--${status.tone}`}>
                  {status.label}
                </span>
              </div>

              <div className="trunfo-steps__item-actions">
                {step.external_url ? (
                  <a
                    className="trunfo-btn"
                    href={step.external_url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {step.external_label && copy.ctaWithLabel
                      ? copy.ctaWithLabel(step.external_label)
                      : copy.cta}{" "}
                    ↗
                  </a>
                ) : null}
                <button
                  type="button"
                  className="trunfo-btn"
                  disabled={isLocked || step.status === "in_progress"}
                  onClick={() => updateStep(step, "in_progress")}
                >
                  Já comecei
                </button>
                <button
                  type="button"
                  className="trunfo-btn"
                  disabled={isLocked || step.status === "skipped"}
                  onClick={() => updateStep(step, "skipped")}
                >
                  Pular
                </button>
                <button
                  type="button"
                  className="trunfo-btn trunfo-btn--primary"
                  disabled={isLocked || step.status === "completed"}
                  onClick={() => updateStep(step, "completed")}
                >
                  Marcar como concluído
                </button>
                {step.status !== "pending" ? (
                  <button
                    type="button"
                    className="trunfo-btn"
                    disabled={isLocked}
                    onClick={() => updateStep(step, "pending")}
                  >
                    Reabrir
                  </button>
                ) : null}
              </div>
              {step.started_at || step.completed_at ? (
                <p className="trunfo-steps__item-meta">
                  {step.started_at ? <span>Iniciado em {formatDate(step.started_at)}</span> : null}
                  {step.completed_at ? (
                    <span> · Concluído em {formatDate(step.completed_at)}</span>
                  ) : null}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>

      <footer className="trunfo-steps__footer">
        {resolved ? (
          <p className="trunfo-steps__resolved">Caso marcado como resolvido.</p>
        ) : (
          <button
            type="button"
            className="trunfo-letter__cta"
            disabled={!canComplete || completing}
            onClick={completeCase}
          >
            {completing ? "Concluindo..." : "Concluir caso"}
          </button>
        )}
      </footer>
    </main>
  );
}

async function postJson(url, body, method) {
  const meta = document.querySelector('meta[name="csrf-token"]');
  const response = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": meta ? meta.content : "",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
